import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import { AuthContext } from '../../../component/context/AuthContext';
import * as CONST from '../../../component/config/Constant';
import { toast } from 'react-toastify';
import { DeleteIcon } from '../../../component/utilities/ActionIcons';
import { IconCheck, IconPlus } from '@tabler/icons-react';

export default function AddQuestionModel({ show, handleClose, onQuestionAdded, editingId = null }) {
    const [authUserData] = useContext(AuthContext);
    const token = authUserData && authUserData[0] ? authUserData[0].token : '';
    const user_id = authUserData && authUserData[0] ? authUserData[0].user_id : '';

    const [questionText, setQuestionText] = useState('');
    const [questionType, setQuestionType] = useState('mcq');
    const [status, setStatus] = useState('active');
    const [options, setOptions] = useState([]);
    const [trueFalseAnswer, setTrueFalseAnswer] = useState('');
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show) {
            if (editingId) {
                fetchQuestion(editingId);
            } else {
                resetForm();
            }
        }
    }, [show, editingId]);

    const resetForm = () => {
        setQuestionText('');
        setQuestionType('mcq');
        setStatus('active');
        setOptions([{ option_text: '', is_correct: false }, { option_text: '', is_correct: false }]);
        setTrueFalseAnswer('');
        setErrors({});
    };

    async function fetchQuestion(id) {
        try {
            const resp = await axios.get(CONST.ISO_GET_QUESTION + id, {
                headers: { Authorization: `Basic ${token}` },
            });

            if (resp.data && resp.data.status === "success" && resp.data.data) {
                const d = resp.data.data;
                setQuestionText(d.question_text || "");
                setQuestionType(d.question_type ? d.question_type.toLowerCase() : "mcq");
                setStatus(d.status ? d.status.toLowerCase() : "active");

                if (d.question_type === 'true_false') {
                    let tfOpt = (d.question_options || d.options || []).find((o) => o.is_correct);
                    setTrueFalseAnswer(tfOpt ? (tfOpt.option_text === 'true' ? 'true' : 'false') : '');
                    setOptions([]);
                } else {
                    let existingOptions = d.question_options || d.options || [];
                    const formattedOptions = existingOptions.map((opt) => ({
                        id: opt.id,
                        question_id: opt.question_id,
                        option_text: opt.option_text || "",
                        is_correct: opt.is_correct === true || opt.is_correct === 1,
                        _delete: false,
                    }));
                    setOptions(formattedOptions);
                    setTrueFalseAnswer('');
                }
            }
        } catch (err) {
            toast.error("Error fetching question.");
        }
    }

    function addOptionField() {
        if (options.filter(o => !o._delete).length >= 4) {
            toast.warning("Maximum 4 options allowed");
            return;
        }
        setOptions([...options, { option_text: '', is_correct: false }]);
    }

    function updateOption(index, key, value) {
        const copy = [...options];
        if (key === "is_correct") {
            copy.forEach((opt, i) => {
                copy[i] = { ...opt, is_correct: i === index };
            });
        } else {
            copy[index] = { ...copy[index], [key]: value };
        }
        setOptions(copy);
        if (errors.options) setErrors({ ...errors, options: "" });
    }

    function removeOption(index) {
        const opt = options[index];
        if (opt && opt.id) {
            const copy = [...options];
            copy[index]._delete = true;
            setOptions(copy);
        } else {
            setOptions(options.filter((_, i) => i !== index));
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        const validationErrors = {};

        if (!questionText || !questionText.trim()) {
            validationErrors.questionText = "Question text is required";
        }

        if (questionType === "mcq") {
            const activeOptions = options.filter(o => !o._delete && o.option_text.trim());
            if (activeOptions.length < 2) {
                validationErrors.options = "At least two options are required";
            } else {
                const optionTexts = activeOptions.map(o => o.option_text.trim().toLowerCase());
                const uniqueOptions = new Set(optionTexts);
                if (uniqueOptions.size !== optionTexts.length) {
                    validationErrors.options = "Duplicate options are not allowed";
                }
                if (!activeOptions.some((opt) => opt.is_correct)) {
                    validationErrors.options = "Select one correct option";
                }
            }
        }

        if (questionType === 'true_false' && !trueFalseAnswer) {
            validationErrors.trueFalseAnswer = 'Select the correct answer';
        }

        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            setSubmitting(false);
            return;
        }

        try {
            const config = { headers: { Authorization: `Basic ${token}` } };
            const payload = {
                question_text: questionText,
                question_type: questionType,
                status: status,
                updated_by: user_id,
                created_by: user_id,
            };

            if (questionType === 'true_false') {
                payload.question_options = [
                    { option_text: 'true', is_correct: trueFalseAnswer === 'true' ? 1 : 0 },
                    { option_text: 'false', is_correct: trueFalseAnswer === 'false' ? 1 : 0 },
                ];
                payload.options = payload.question_options;
            } else {
                payload.question_options = options
                    .filter((o) => o.option_text && !o._delete)
                    .map((o) => ({
                        id: o.id,
                        option_text: o.option_text,
                        is_correct: o.is_correct ? 1 : 0,
                    }));
                payload.options = payload.question_options;
            }

            const resp = editingId
                ? await axios.put(CONST.ISO_UPDATE_QUESTION + editingId, payload, config)
                : await axios.post(CONST.ISO_ADD_QUESTION, payload, config);

            if (resp.data && resp.data.status === "success") {
                toast.success(editingId ? "Question updated" : "Question added");
                const rawData = resp.data.data;
                const newId = Array.isArray(rawData) ? (rawData[0]?.id || rawData[0]) : (rawData?.id || rawData);
                if (onQuestionAdded) onQuestionAdded(newId || editingId);
                handleClose();
            } else {
                toast.error(resp.data.message || "Operation failed");
            }
        } catch (err) {
            toast.error("Save failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal size="lg" show={show} onHide={handleClose} centered>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fw-bold">{editingId ? "Edit Question" : "Add New Question"}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="form-label required">Question Text</label>
                        <textarea
                            className={`form-control ${errors.questionText ? 'is-invalid' : ''}`}
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                            rows="3"
                            placeholder="Type your question here..."
                        />
                        {errors.questionText && <div className="invalid-feedback">{errors.questionText}</div>}
                    </div>

                    <div className="row g-3 mb-4">
                        <div className="col-md-6">
                            <label className="form-label required">Question Type</label>
                            <select
                                className="form-select"
                                value={questionType}
                                onChange={(e) => setQuestionType(e.target.value)}
                            >
                                <option value="mcq">Multiple Choice (MCQ)</option>
                                <option value="true_false">True or False</option>
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label required">Status</label>
                            <select
                                className="form-select"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    {questionType === 'true_false' && (
                        <div className="card bg-light border-0 mb-3">
                            <div className="card-body">
                                <label className="form-label fw-bold mb-3">Select Correct Answer</label>
                                <div className="d-flex gap-4">
                                    <label className="form-check cursor-pointer">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="tf"
                                            value="true"
                                            checked={trueFalseAnswer === 'true'}
                                            onChange={e => setTrueFalseAnswer(e.target.value)}
                                        />
                                        <span className="form-check-label mr-2">True</span>
                                    </label>
                                    <label className="form-check cursor-pointer">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="tf"
                                            value="false"
                                            checked={trueFalseAnswer === 'false'}
                                            onChange={e => setTrueFalseAnswer(e.target.value)}
                                        />
                                        <span className="form-check-label">False</span>
                                    </label>
                                </div>
                                {errors.trueFalseAnswer && <div className="text-danger small mt-2">{errors.trueFalseAnswer}</div>}
                            </div>
                        </div>
                    )}

                    {questionType === "mcq" && (
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <label className="form-label fw-bold mb-0">Answers Options</label>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
                                    onClick={addOptionField}
                                >
                                    <IconPlus size={16} /> Add Option
                                </button>
                            </div>
                            <div className="row g-2">
                                {options.map((option, index) => (
                                    !option._delete && (
                                        <div key={index} className="col-12 d-flex gap-2 align-items-center mb-2">
                                            <input
                                                type="text"
                                                className="form-control mr-2"
                                                value={option.option_text}
                                                onChange={(e) => updateOption(index, 'option_text', e.target.value)}
                                                placeholder={`Option ${index + 1}`}
                                            />
                                            <button
                                                type="button"
                                                className={`btn btn-icon ${option.is_correct ? "btn-success" : "btn-outline-secondary"}`}
                                                onClick={() => updateOption(index, 'is_correct', !option.is_correct)}
                                                title="Mark as correct"
                                                style={{ minWidth: '38px' }}
                                            >
                                                <IconCheck size={18} />
                                            </button>
                                            <DeleteIcon onClick={() => removeOption(index)} tooltip="Remove Option" />
                                        </div>
                                    )
                                ))}
                            </div>
                            {errors.options && <div className="text-danger small mt-2">{errors.options}</div>}
                        </div>
                    )}
                </form>
            </Modal.Body>
            <Modal.Footer className="bg-light p-3">
                <Button variant="link" className="text-muted text-decoration-none" onClick={handleClose}>
                    Discard
                </Button>
                <Button variant="primary" className="px-4" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? "Saving..." : "Save Question"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
